import { useCallback, useEffect, useRef, useState } from 'react';
import { listUserFiles, type TreeNode } from '../../agents/userFilesApi.ts';

/**
 * Owns the console's Files-panel state: the signed-in user's document tree,
 * loading/error status, and a `reload` to refresh after the agent creates or
 * deletes a file in chat. Lifted by `AgentConsole`, which feeds the tree into
 * `InspectorPanel` → `FilesPanel`.
 *
 * The listing is user-scoped (not agent-scoped), so it loads once on mount and
 * on explicit `reload`. A `reload` counter guards against an out-of-order fetch
 * landing after a newer one.
 */
export interface UseUserFiles {
  readonly tree: readonly TreeNode[];
  readonly loading: boolean;
  readonly error: string | null;
  /** Re-fetch the tree (e.g. after a chat-driven save/delete completes). */
  reload: () => void;
}

export function useUserFiles(): UseUserFiles {
  const [tree, setTree] = useState<readonly TreeNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Monotonic request id — only the latest fetch is allowed to set state, so a
  // slow earlier request can't overwrite a newer result.
  const reqRef = useRef(0);

  const load = useCallback(() => {
    const reqId = ++reqRef.current;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const rows = await listUserFiles();
        if (reqRef.current === reqId) {
          setTree(rows);
        }
      } catch (err) {
        if (reqRef.current === reqId) {
          setError(err instanceof Error ? err.message : 'Could not load your files.');
        }
      } finally {
        if (reqRef.current === reqId) {
          setLoading(false);
        }
      }
    })();
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { tree, loading, error, reload: load };
}
